import React from "react";
import { SocialNetworksId } from "@/interfaces";
import { SocialSvg } from "@/components/SocialSvg";
import { Review } from "@/services/bedrock";
import { kFormatter } from "@/utils";

export const SOURCE_SYSTEM_CONFIG: Record<
  string,
  { name: string; bg: string; text: string; border: string; brandBg: string; isInternal: boolean }
> = {
  FB: {
    name: "Facebook",
    bg: "bg-[#1877f2]/10 text-[#1877f2] border-[#1877f2]/20",
    text: "text-[#1877f2]",
    border: "border-[#1877f2]/20",
    brandBg: "bg-[#1877f2] text-white",
    isInternal: false,
  },
  ZL: {
    name: "Zillow",
    bg: "bg-[#0041D9]/10 text-[#0041D9] border-[#0041D9]/20",
    text: "text-[#0041D9]",
    border: "border-[#0041D9]/20",
    brandBg: "bg-[#0041D9] text-white",
    isInternal: false,
  },
  GL: {
    name: "Google",
    bg: "bg-[#EA4335]/10 text-[#EA4335] border-[#EA4335]/20",
    text: "text-[#EA4335]",
    border: "border-[#EA4335]/20",
    brandBg: "bg-[#EA4335] text-white",
    isInternal: false,
  },
  SC: {
    name: "SocialCoach Review",
    bg: "bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20",
    text: "text-[#7C3AED]",
    border: "border-[#7C3AED]/20",
    brandBg: "bg-[#7C3AED] text-white",
    isInternal: true,
  },
  BE: {
    name: "Imported Review",
    bg: "bg-[#0D6EFD]/10 text-[#0D6EFD] border-[#7C3AED]/20",
    text: "text-[#0D6EFD]",
    border: "border-[#0D6EFD]/20",
    brandBg: "bg-[#0D6EFD] text-white",
    isInternal: true,
  },
  EX: {
    name: "Imported Review",
    bg: "bg-[#1B62A9]/10 text-[#1B62A9] border-[#1B62A9]/20",
    text: "text-[#1B62A9]",
    border: "border-[#1B62A9]/20",
    brandBg: "bg-[#1B62A9] text-white",
    isInternal: true,
  },
  IF: {
    name: "Imported Review",
    bg: "bg-slate-700/10 text-slate-700 border-slate-700/20",
    text: "text-slate-700",
    border: "border-slate-700/20",
    brandBg: "bg-slate-700 text-white",
    isInternal: true,
  },
};

export function getSourceIcon(sourceSystem: string, size: "xs" | "sm" | "md" | "lg" = "xs"): React.ReactNode {
  return <SocialSvg networkId={sourceSystem as SocialNetworksId} size={size} variant="icon" />;
}

export type ReviewTone = "positive" | "caution" | "negative" | "muted" | "subtle";

/** Single source of truth for review chip colors, so sentiment and reply status stay in sync. */
export const REVIEW_TONE_CLASSES: Record<ReviewTone, { badge: string; icon: string }> = {
  positive: {
    badge: "bg-emerald-500 hover:bg-emerald-500 border-emerald-500 text-white",
    icon: "text-emerald-500",
  },
  caution: {
    badge: "bg-yellow-500 hover:bg-yellow-500 border-yellow-500 text-white",
    icon: "text-yellow-500",
  },
  negative: {
    badge: "bg-rose-500 hover:bg-rose-500 border-rose-500 text-white",
    icon: "text-rose-500",
  },
  muted: {
    badge: "bg-slate-500 hover:bg-slate-500 border-slate-500 text-white",
    icon: "text-slate-500",
  },
  subtle: {
    badge: "bg-slate-100 hover:bg-slate-100 border-slate-200 text-slate-600",
    icon: "text-slate-400",
  },
};

export type SentimentType = "positive" | "neutral" | "negative";

export function getReviewSentiment(review: Review): SentimentType {
  // 1. Explicit sentiment string from API
  if (review.sentiment === "positive") return "positive";
  if (review.sentiment === "negative") return "negative";
  if (review.sentiment === "neutral") return "neutral";

  // 2. Implied sentiment by Star Rating (Out of 5)
  if (review.starRating) {
    if (review.starRating === 5) return "positive";
    if (review.starRating <= 2) return "negative";
    return "neutral"; // 3 and 4 stars are neutral
  }

  // 3. Implied sentiment by NPS (Out of 10)
  if (review.npsScore !== undefined && review.npsScore !== null) {
    if (review.npsScore >= 9) return "positive";
    if (review.npsScore <= 6) return "negative";
    return "neutral";
  }

  // 4. Implied sentiment by Ten Point (Out of 10)
  if (review.tenPointRating !== undefined && review.tenPointRating !== null) {
    if (review.tenPointRating >= 8) return "positive";
    if (review.tenPointRating <= 5) return "negative";
    return "neutral";
  }

  // Fallback to neutral if we literally have no scoring data
  return "neutral";
}

export function getSentimentStyles(sentiment: SentimentType) {
  switch (sentiment) {
    case "positive":
      return {
        unselectedBorder: "border-l-emerald-200",
        unselectedHover: "hover:bg-emerald-50/40 hover:border-l-emerald-300",
        selectedBg: "bg-emerald-50/70 border-l-emerald-500 border-y-emerald-200 border-r-emerald-200",
        selectedText: "text-emerald-950",
        avatarGradient: "bg-gradient-to-br from-emerald-400 to-teal-500",
      };
    case "negative":
      return {
        unselectedBorder: "border-l-rose-200",
        unselectedHover: "hover:bg-rose-50/40 hover:border-l-rose-300",
        selectedBg: "bg-rose-50/70 border-l-rose-500 border-y-rose-200 border-r-rose-200",
        selectedText: "text-rose-950",
        avatarGradient: "bg-gradient-to-br from-rose-400 to-pink-500",
      };
    case "neutral":
    default:
      return {
        unselectedBorder: "border-l-yellow-200",
        unselectedHover: "hover:bg-yellow-50/50 hover:border-l-yellow-400",
        selectedBg: "bg-yellow-50/80 border-l-yellow-500 border-y-yellow-200 border-r-yellow-200",
        selectedText: "text-yellow-950",
        avatarGradient: "bg-gradient-to-br from-yellow-400 to-yellow-500",
      };
  }
}

export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    if (diffMs < 0) return "just now";
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSecs < 60) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${diffWeeks}w ago`;
    if (diffDays < 365) return `${diffMonths}mo ago`;
    return `${Math.max(1, diffYears)}y ago`;
  } catch (error) {
    return "";
  }
}

export function getInitials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function getInternalBadgeName(review: Review) {
  if (review.sourceSystem === "SC" && review.ratingType === "star") return "SocialCoach Review";
  if (review.sourceSystem === "SC") return "SocialCoach Survey";
  return "Imported Review";
}

/** Compact review count label, e.g. "1 review", "2 reviews", "102.5k reviews". */
export function formatReviewCount(count: number): string {
  if (count === 1) return "1 review";
  const formatted = count === 0 ? "0" : kFormatter(count);
  return `${formatted} reviews`;
}

/** Scores at or above this threshold are marked Featured in Coach review UI and exports. */
export const FEATURED_SHAREABILITY_MIN_SCORE = 90;
